import { HPONode } from '@/features/terminology/hpo/HPOSearchService'

// Type definitions for term structures
export interface Term {
  id: string
  label: string
  description?: string
  category: 'Key' | 'Additional' | 'Excluded'
}

export interface TermsByCategory {
  Key: Term[]
  Additional: Term[]
  Excluded: Term[]
}

export enum Sex {
  Male = 'male',
  Female = 'female',
  Ambiguous = 'ambiguous',
  Unknown = '',
}

export enum TestType {
  urWGS = 'F11-03',
  rWGS_Patient_Only = 'F10-01',
  rWGS_Duo = 'F10-02',
  rWGS_Trio = 'F10-03',
  NONE = '',
}

export enum CollectionType {
  WholeBloodEDTA = 'whole-blood-edta',
  Buccal = 'buccal',
  Saliva = 'saliva',
  Unknown = '',
}

export function getCollectionTypeLabel(collectionType: CollectionType): string {
  switch (collectionType) {
    case CollectionType.WholeBloodEDTA:
      return 'Whole Blood (EDTA)'
    case CollectionType.Saliva:
      return 'Saliva (confirmatory testing only)'
    default:
      return ''
  }
}

export enum PhenotypicTrait {
  ClinicallyAffected = 'Clinically Affected',
  ClinicallyUnaffected = 'Clinically Unaffected',
  Unknown = '',
}

// Explicit form data interfaces
export interface OrderData {
  selectedOption: TestType
}

export interface PatientData {
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  sex: Sex
  mrn: string
  collectionType: CollectionType
}

export interface ClinicalData {
  clinicalNote: string
  optOutClinicianNotes: boolean
  construeResults?: {
    terms?: Array<{ id: string; label: string; description?: string }>
    message?: string
    success?: boolean
  }
}

export interface CodeableConcept {
  id: string
  label: string
  description?: string
  category: 'Key' | 'Additional' | 'Excluded'
}

// HPO Option interface for the search component
export interface HPOOption {
  id: string
  label: string
  node: HPONode
}

// Extended HPO option with category
export interface CategorizedHPOOption extends HPOOption {
  category: 'Key' | 'Additional' | 'Excluded'
}

export interface ReasonForTestingData {
  clinicalDiagnosis: CategorizedHPOOption[]
  icd10Diagnosis: string | Array<CodeableConcept>
  userInteracted: boolean
  categorizedTerms?: Array<CategorizedHPOOption>
  construeResults?: {
    terms?: Array<{ id: string; label: string; description?: string }>
    message?: string
    success?: boolean
  }
}

export interface FamilyHistoryData {
  familyHistory: string
  hasSibling: boolean
  hasParent: boolean
  hasGrandparent: boolean
  hasCousin: boolean
}

export interface ConsentData {
  providerName: string
  providerEmail: string
  envelopeId: string
  status: string
  isSigningComplete: boolean
}

// Main form data interface
export interface RequisitionFormData {
  order: OrderData
  patient: PatientData
  clinical: ClinicalData
  reason: ReasonForTestingData
  family: FamilyHistoryData
  mother?: BiologicalParentData
  father?: BiologicalParentData
  consent: ConsentData
  consentFormData?: {
    medicalProfessionalName: string
    npi: string
    signatureDate: string
    signature: string
  }
  version: 1
}

// Import BiologicalParentData from the existing file
export interface BiologicalParentData {
  parentType: 'mother' | 'father'
  parentFirstName: string
  parentLastName: string
  parentDateOfBirth: string
  parentMRN: string
  parentPhone: string
  parentEmail: string
  phenotypicTrait: PhenotypicTrait
  hpoTerms: CategorizedHPOOption[]
  icd10Terms: string
  collectionType: CollectionType
}

// Default values for form data
export const DEFAULT_ORDER_DATA: OrderData = {
  selectedOption: TestType.NONE,
}

export const DEFAULT_PATIENT_DATA: PatientData = {
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: '',
  sex: Sex.Unknown,
  mrn: '',
  collectionType: CollectionType.Unknown,
}

export const DEFAULT_CLINICAL_DATA: ClinicalData = {
  clinicalNote: '',
  optOutClinicianNotes: false,
}

export const DEFAULT_REASON_DATA: ReasonForTestingData = {
  clinicalDiagnosis: [],
  icd10Diagnosis: '',
  userInteracted: false,
}

export const DEFAULT_FAMILY_DATA: FamilyHistoryData = {
  familyHistory: '',
  hasSibling: false,
  hasParent: false,
  hasGrandparent: false,
  hasCousin: false,
}

export const DEFAULT_MOTHER_DATA: BiologicalParentData = {
  parentType: 'mother',
  parentFirstName: '',
  parentLastName: '',
  parentDateOfBirth: '',
  parentMRN: '',
  parentPhone: '',
  parentEmail: '',
  phenotypicTrait: PhenotypicTrait.Unknown,
  hpoTerms: [],
  icd10Terms: '',
  collectionType: CollectionType.Unknown,
}

export const DEFAULT_FATHER_DATA: BiologicalParentData = {
  parentType: 'father',
  parentFirstName: '',
  parentLastName: '',
  parentDateOfBirth: '',
  parentMRN: '',
  parentPhone: '',
  parentEmail: '',
  phenotypicTrait: PhenotypicTrait.Unknown,
  hpoTerms: [],
  icd10Terms: '',
  collectionType: CollectionType.Unknown,
}

export const DEFAULT_CONSENT_DATA: ConsentData = {
  providerName: '',
  providerEmail: '',
  envelopeId: '',
  status: '',
  isSigningComplete: false,
}

export const DEFAULT_FORM_DATA: RequisitionFormData = {
  order: DEFAULT_ORDER_DATA,
  patient: DEFAULT_PATIENT_DATA,
  clinical: DEFAULT_CLINICAL_DATA,
  reason: DEFAULT_REASON_DATA,
  family: DEFAULT_FAMILY_DATA,
  mother: DEFAULT_MOTHER_DATA,
  father: DEFAULT_FATHER_DATA,
  consent: DEFAULT_CONSENT_DATA,
  version: 1,
}

export interface PhysicianInfo {
  id: string
  name: {
    firstName: string
    lastName: string
  }
  roleTitle: string
  phone: string
  email: string
}

export interface ParentSample {
  name: {
    firstName: string
    lastName: string
  }
  dob?: string
  mrn?: string
}

export interface OrderFormInfoData {
  patient: {
    id: string
    name: {
      firstName: string
      middleName?: string
      lastName: string
      alias?: string
    }
    mrn: string
    sex: Sex
    birthDate: Date | null
  }
  institution?: {
    name: string
    address: {
      street: string
      city: string
      state: string
      zip: string
      country: string
    }
  }
  orderingProvider?: PhysicianInfo
  patientPhysician?: PhysicianInfo
  parentSamples?: {
    mother?: ParentSample
    father?: ParentSample
    other?: ParentSample
  }
  medicalProfessionalInfo: {
    npi: string
    name: string
    email?: string
  }
}
